/**
 * Phase 6-C — Client Portal API client.
 *
 * Thin fetch wrapper mirroring `identity-api.ts`. RTK Query intentionally not
 * used (no other slice uses it yet — see CLAUDE.md "Vite migration deferred").
 *
 * Base URL: `CENTRAL_API_BASE_URL` from `src/lib/constants/central-api.ts`,
 * which already resolves to `${env-base}/api/v1/`.
 *
 * Auth: callers pass the per-session magic-link access token explicitly so
 * this module never reads from Redux — that way the MagicLink page can call
 * `authenticate(token)` BEFORE the token is committed to the store.
 *
 * Per CLAUDE.md and the Phase 6-C brief, the client-portal access token is
 * NEVER persisted to localStorage.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import type { ClientPortalIdentity } from '../../../store/reducers/authSlice';

// ---------- shared types ----------

export interface ClientPortalAuthResponse {
  access_token: string;
  expires_at: number; // epoch ms
  client: ClientPortalIdentity;
}

// OTP login — request-otp always 200s (no account enumeration); it only
// actually emails a code when the client was already invited to the portal.
export interface ClientPortalRequestOtpResponse {
  sent: boolean;
  expires_at: number; // epoch ms — when the code expires
}

export interface ClientPortalProposal {
  id: string;
  title?: string;
  status?: string;
  created_at?: string;
  // Phase 6 returns a partial card; keep wide so we don't fight the backend.
  [key: string]: unknown;
}

export interface ClientPortalReport {
  id: string;
  title?: string;
  drive_url?: string;
  generated_at?: string;
  [key: string]: unknown;
}

// ---------- helpers ----------

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[client-portal-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  accessToken?: string
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((init.headers as Record<string, string>) || {}),
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<T>(response);
}

// ---------- endpoints ----------

/** POST /api/v1/client-portal/auth — exchange a magic-link token for an access token. */
export async function authenticate(
  token: string
): Promise<ClientPortalAuthResponse> {
  return request<ClientPortalAuthResponse>('client-portal/auth', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/**
 * POST /api/v1/client-portal/request-otp — email a one-time login code.
 *
 * Always resolves 200 `{ sent: true, expires_at }` regardless of whether the
 * email maps to an invited client (no account enumeration). The backend only
 * actually sends an email when the client already has portal access, and it
 * rate-limits resends silently.
 */
export async function requestOtp(
  email: string,
  shopSlug?: string
): Promise<ClientPortalRequestOtpResponse> {
  const body: Record<string, string> = { email };
  if (shopSlug) body.shop_slug = shopSlug;
  return request<ClientPortalRequestOtpResponse>('client-portal/request-otp', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * POST /api/v1/client-portal/verify-otp — exchange an emailed code for an
 * access token. Returns the same `ClientPortalAuthResponse` shape as
 * `authenticate`. On a wrong/expired code the backend responds 401
 * `{ error: "invalid_or_expired_code" }`, which surfaces here as an
 * `Error & { status: 401 }` (matching the `request()` helper's contract).
 */
export async function verifyOtp(
  email: string,
  code: string
): Promise<ClientPortalAuthResponse> {
  return request<ClientPortalAuthResponse>('client-portal/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
}

/** GET /api/v1/client-portal/me — identity card for the logged-in client. */
export async function getMe(
  accessToken: string
): Promise<ClientPortalIdentity> {
  return request<ClientPortalIdentity>(
    'client-portal/me',
    { method: 'GET' },
    accessToken
  );
}

/** GET /api/v1/client-portal/proposals — client-visible proposals (limit 50). */
export async function getProposals(
  accessToken: string
): Promise<ClientPortalProposal[]> {
  const res = await request<
    { proposals?: ClientPortalProposal[] } | ClientPortalProposal[]
  >('client-portal/proposals', { method: 'GET' }, accessToken);
  return Array.isArray(res) ? res : res.proposals ?? [];
}

/** GET /api/v1/client-portal/reports — Phase 6 returns an empty list. */
export async function getReports(
  accessToken: string
): Promise<ClientPortalReport[]> {
  const res = await request<
    { reports?: ClientPortalReport[] } | ClientPortalReport[]
  >('client-portal/reports', { method: 'GET' }, accessToken);
  return Array.isArray(res) ? res : res.reports ?? [];
}

// ---------- agency-side invite (employee-auth) ----------

export interface InviteClientRequest {
  shop_slug: string;
  email: string;
  name?: string;
}

export interface InviteClientResponse {
  magic_link_url: string;
  expires_at: number;
}

/**
 * POST /api/v1/client-portal/invite — agency employee creates a magic link.
 * Uses the employee Bearer token (passed in by caller).
 */
export async function inviteClient(
  employeeToken: string,
  body: InviteClientRequest
): Promise<InviteClientResponse> {
  return request<InviteClientResponse>(
    'client-portal/invite',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    employeeToken
  );
}
